<div class="container-fluid p-3 ahe-page">

  <!-- Header card -->
  <div class="card shadow-sm mb-3 ahe-header-card">
    <div class="card-body d-flex align-items-center justify-content-between flex-wrap gap-3">
      <div class="d-flex align-items-center gap-3">
        <div class="ahe-header-icon">
          <i class="bi bi-diagram-3"></i>
        </div>
        <div>
          <h2 class="fw-bold mb-0">Attachment Folders</h2>
          <small class="text-muted">Manage the folder hierarchy used to organize client attachments.</small>
        </div>
      </div>

      <div class="d-flex align-items-center gap-2" *ngIf="isGlobalAdmin">
        <label class="form-label small fw-semibold text-muted mb-0 me-1" for="ahe-client-select">
          Client
        </label>
        <select id="ahe-client-select"
                class="form-select form-select-sm ahe-client-select"
                [ngModel]="selectedClientId"
                (ngModelChange)="onClientChange($event)">
          <option [ngValue]="null">— Select a client —</option>
          <option *ngFor="let c of clients" [ngValue]="c.id">{{ c.displayName }}</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Empty: no client picked -->
  <div *ngIf="!selectedClientId" class="card shadow-sm">
    <div class="card-body text-center py-5 text-muted">
      <i class="bi bi-folder2-open ahe-empty-icon"></i>
      <div class="mt-2">Select a client to manage its attachment hierarchies.</div>
    </div>
  </div>

  <!-- Main editor -->
  <div *ngIf="selectedClientId" class="card shadow-sm ahe-main-card">

    <div class="card-header bg-white py-3">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">

        <div class="ahe-new-root">
          <div class="input-group input-group-sm">
            <span class="input-group-text bg-white">
              <i class="bi bi-folder-plus text-muted"></i>
            </span>
            <input class="form-control"
                   placeholder="New top-level folder name…"
                   [(ngModel)]="newRootName"
                   (keyup.enter)="addRoot()" />
            <button class="btn btn-primary"
                    [disabled]="saving || !newRootName.trim()"
                    (click)="addRoot()">
              <i class="bi bi-plus-lg me-1"></i> Add Folder
            </button>
          </div>
        </div>

        <button class="btn btn-sm btn-outline-secondary"
                [disabled]="loading"
                (click)="loadHierarchies()"
                title="Refresh">
          <i class="bi bi-arrow-clockwise me-1"
             [class.ahe-spin]="loading"></i>
          {{ loading ? 'Refreshing…' : 'Refresh' }}
        </button>

      </div>
    </div>

    <div class="card-body p-0 ahe-body">

      <div *ngIf="loading" class="ahe-status">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <span>Loading hierarchies…</span>
      </div>

      <div *ngIf="!loading && tree.length === 0" class="ahe-status ahe-status-empty">
        <i class="bi bi-folder-x ahe-empty-icon"></i>
        <div>No folders defined yet.</div>
        <small class="text-muted">Add a top-level folder above to get started.</small>
      </div>

      <ul *ngIf="!loading && tree.length > 0"
          class="ahe-tree list-unstyled mb-0">
        <ng-container *ngFor="let n of flatten(); trackBy: trackByNode">

          <li class="ahe-row"
              [class.ahe-row-editing]="n.editing"
              [style.padding-left.px]="14 + n.depth * 22">

            <span class="ahe-caret">
              <button *ngIf="n.children.length > 0"
                      class="ahe-caret-btn"
                      (click)="toggleExpand(n)"
                      [attr.aria-label]="n.expanded ? 'Collapse' : 'Expand'">
                <i class="bi"
                   [ngClass]="n.expanded ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
              </button>
            </span>

            <i class="bi ahe-folder-icon"
               [ngClass]="n.children.length && n.expanded ? 'bi-folder2-open' : 'bi-folder-fill'"></i>

            <ng-container *ngIf="!n.editing">
              <span class="ahe-name">{{ n.folderName }}</span>
              <span *ngIf="n.children.length > 0" class="ahe-count">
                {{ n.children.length }}
              </span>
            </ng-container>

            <input *ngIf="n.editing"
                   class="form-control form-control-sm ahe-inline-input"
                   [(ngModel)]="n.editValue"
                   (keyup.enter)="commitRename(n)"
                   (keyup.escape)="cancelRename(n)" />

            <span class="flex-grow-1"></span>

            <div class="ahe-actions">
              <ng-container *ngIf="!n.editing">
                <button class="ahe-action-btn"
                        title="Add subfolder"
                        [disabled]="saving"
                        (click)="startAddChild(n)">
                  <i class="bi bi-folder-plus"></i>
                </button>
                <button class="ahe-action-btn"
                        title="Rename"
                        [disabled]="saving"
                        (click)="startRename(n)">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="ahe-action-btn ahe-action-danger"
                        title="Delete"
                        [disabled]="saving"
                        (click)="delete(n)">
                  <i class="bi bi-trash"></i>
                </button>
              </ng-container>

              <ng-container *ngIf="n.editing">
                <button class="btn btn-sm btn-success ahe-confirm"
                        [disabled]="saving || !n.editValue.trim()"
                        (click)="commitRename(n)"
                        title="Save">
                  <i class="bi bi-check-lg"></i>
                </button>
                <button class="btn btn-sm btn-outline-secondary"
                        [disabled]="saving"
                        (click)="cancelRename(n)"
                        title="Cancel">
                  <i class="bi bi-x-lg"></i>
                </button>
              </ng-container>
            </div>
          </li>

          <li *ngIf="n.addingChild"
              class="ahe-row ahe-row-draft"
              [style.padding-left.px]="14 + (n.depth + 1) * 22">
            <span class="ahe-caret"></span>
            <i class="bi bi-folder-plus ahe-folder-icon ahe-draft-icon"></i>
            <input class="form-control form-control-sm ahe-inline-input"
                   [(ngModel)]="n.childDraft"
                   (keyup.enter)="commitAddChild(n)"
                   (keyup.escape)="cancelAddChild(n)"
                   placeholder="New subfolder name…" />
            <span class="flex-grow-1"></span>
            <div class="ahe-actions">
              <button class="btn btn-sm btn-success ahe-confirm"
                      [disabled]="saving || !n.childDraft.trim()"
                      (click)="commitAddChild(n)"
                      title="Create">
                <i class="bi bi-check-lg"></i>
              </button>
              <button class="btn btn-sm btn-outline-secondary"
                      [disabled]="saving"
                      (click)="cancelAddChild(n)"
                      title="Cancel">
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </li>

        </ng-container>
      </ul>
    </div>

    <div class="card-footer bg-white d-flex align-items-center gap-3 small text-muted ahe-footer">
      <span>
        <i class="bi bi-info-circle me-1"></i>
        Folders containing attachments (or attachments in any subfolder) cannot be deleted.
      </span>
      <span class="ms-auto">
        {{ flat.length }} folder{{ flat.length === 1 ? '' : 's' }}
      </span>
    </div>
  </div>
</div>
